<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { EChartsOption } from 'echarts'
import areaCodeEn from '~/assets/json/areaCodeEn.json'
import type { TraderMarketInfluenceItem } from '~/server/detail'
import { useEcharts } from '~/composables/useEcharts'

interface CountryScoreItem {
  id: string
  name: string
  score: string
  value: number
  mapName: string
  mapFeatureName: string
  tooltipCenter?: [number, number]
  independentHighlight?: boolean
  tooltipName?: string
  tooltipValue?: string
  flag?: string
  flagBg?: string
}

type CountryCodeItem = {
  countryCode?: string
  name?: string
  twoCharCode?: string
}

type CountryCodeGroup = {
  data?: CountryCodeItem[]
}

type EchartsMouseParams = {
  name?: string
  event?: {
    offsetX?: number
    offsetY?: number
  }
}

type GeoJsonFeature = {
  type?: string
  properties?: {
    name?: string
  }
  geometry?: {
    coordinates?: unknown
  }
}

type RegisteredWorldMap = {
  geoJSON?: {
    features?: GeoJsonFeature[]
  }
  geoJson?: {
    features?: GeoJsonFeature[]
  }
}

const WORLD_MAP_CDN_URL = 'https://resources1.interface003.com/web20/word.js'
const MAP_TOOLTIP_HALF_WIDTH = 75
const INDEPENDENT_REGION_TOOLTIP_GAP = 18
const mapCountryCenterFallbacks: Record<string, [number, number]> = {
  China: [104.195397, 35.86166],
  Bangladesh: [90.3563, 23.685],
}
let worldMapLoadPromise: Promise<void> | null = null

const props = withDefaults(
  defineProps<{
    influenceRating?: string
    marketInfluenceCountries?: TraderMarketInfluenceItem[] | null
  }>(),
  {
    influenceRating: '',
    marketInfluenceCountries: () => [],
  },
)

const { t } = useAppI18n()
const { isRtl } = useRtl()
const normalizeText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())

const countryNameByNumericCode = new Map(
  (areaCodeEn as CountryCodeGroup[])
    .flatMap((group) => group.data || [])
    .map((item) => [normalizeText(item.countryCode), normalizeText(item.name)] as const)
    .filter(([countryCode, name]) => countryCode && name),
)

const countryNameByTwoCharCode = new Map(
  (areaCodeEn as CountryCodeGroup[])
    .flatMap((group) => group.data || [])
    .map((item) => [normalizeText(item.twoCharCode).toLowerCase(), normalizeText(item.name)] as const)
    .filter(([twoCharCode, name]) => twoCharCode && name),
)

const echartsMapNameByCountryCode: Record<string, string> = {
  '050': 'Bangladesh',
  '070': 'Bosnia and Herz.',
  '140': 'Central African Rep.',
  '158': 'Taiwan',
  '156': 'China',
  '180': 'Dem. Rep. Congo',
  '203': 'Czech Rep.',
  '222': 'El Salvador',
  '226': 'Eq. Guinea',
  '242': 'Fiji',
  '384': "Côte d'Ivoire",
  '583': 'Micronesia',
  '626': 'Timor-Leste',
  '090': 'Solomon Is.',
  '344': 'Hong Kong',
  '840': 'United States',
  ba: 'Bosnia and Herz.',
  bd: 'Bangladesh',
  cf: 'Central African Rep.',
  ci: "Côte d'Ivoire",
  cn: 'China',
  cd: 'Dem. Rep. Congo',
  cz: 'Czech Rep.',
  fj: 'Fiji',
  fm: 'Micronesia',
  gq: 'Eq. Guinea',
  gb: 'United Kingdom',
  hk: 'Hong Kong',
  kr: 'Korea',
  sb: 'Solomon Is.',
  sv: 'El Salvador',
  tl: 'Timor-Leste',
  tw: 'Taiwan',
  uk: 'United Kingdom',
  us: 'United States',
}

const mapFeatureOverrideByCountryCode: Record<
  string,
  { featureName: string; tooltipCenter?: [number, number]; independentHighlight?: boolean }
> = {
  '050': { featureName: 'Bangladesh', tooltipCenter: [90.3563, 23.685] },
  '158': { featureName: 'Taiwan', tooltipCenter: [120.9605, 23.6978], independentHighlight: true },
  '344': { featureName: 'Hong Kong', tooltipCenter: [114.1694, 22.3193], independentHighlight: true },
  bd: { featureName: 'Bangladesh', tooltipCenter: [90.3563, 23.685] },
  hk: { featureName: 'Hong Kong', tooltipCenter: [114.1694, 22.3193], independentHighlight: true },
  tw: { featureName: 'Taiwan', tooltipCenter: [120.9605, 23.6978], independentHighlight: true },
}

const normalizeIndexValue = (value: unknown) => {
  const parsed = Number.parseFloat(normalizeText(value))

  return Number.isFinite(parsed) ? parsed : null
}

const formatScore = (value: number | null, fallback: unknown) => {
  if (value !== null && Number.isFinite(value)) {
    return value.toFixed(2)
  }

  return normalizeText(fallback)
}

const getMapAreaColor = (value: number) => {
  if (value > 8) return '#e83132'
  if (value > 6) return '#e83132cc'
  if (value > 4) return '#e8313299'
  if (value > 2) return '#e8313266'
  if (value > 0) return '#e8313233'
  return '#e6e7eb'
}

const topCountries = computed<CountryScoreItem[]>(() => {
  return (props.marketInfluenceCountries || [])
    .map((item, index) => {
      const countryCode = normalizeText(item.countryCode).toLowerCase()
      const value = normalizeIndexValue(item.index)
      const name = normalizeText(item.countryName) || countryCode.toUpperCase()
      const score = formatScore(value, item.index)
      const mapName =
        echartsMapNameByCountryCode[countryCode] ||
        countryNameByNumericCode.get(countryCode) ||
        countryNameByTwoCharCode.get(countryCode) ||
        name
      const mapFeatureOverride = mapFeatureOverrideByCountryCode[countryCode]

      return {
        id: countryCode || `country-${index}`,
        name,
        score,
        value: value ?? 0,
        mapName,
        mapFeatureName: mapFeatureOverride?.featureName || mapName,
        tooltipCenter: mapFeatureOverride?.tooltipCenter,
        independentHighlight: mapFeatureOverride?.independentHighlight,
        tooltipName: name,
        tooltipValue: score,
        flag: normalizeText(item.countryFlag) || undefined,
      }
    })
    .filter((item) => item.id && item.name)
})

const showInfluenceRating = computed(() => props.influenceRating.trim().length > 0)
const hoveredCountryId = ref('')
const mapBoxRef = ref<HTMLElement | null>(null)
const mapChartRef = ref<HTMLElement | null>(null)
const countryScoreListRef = ref<HTMLElement | null>(null)
const lastScrolledCountryId = ref('')
const mapZoom = ref(1)
const mapChartReady = ref(false)
const mapChartLoadFailed = ref(false)
const tooltip = ref({
  show: false,
  x: 0,
  y: 0,
  countryId: '',
})
const countryScoreScrollMetrics = ref({
  scrollTop: 0,
  scrollHeight: 0,
  clientHeight: 0,
})

const mapStyle = computed(() => ({
  transform: 'none',
}))

const topCountryById = computed(() => {
  return topCountries.value.reduce<Record<string, CountryScoreItem>>((acc, item) => {
    acc[item.id] = item
    return acc
  }, {})
})

const topCountryByMapName = computed(() => {
  return topCountries.value.reduce<Record<string, CountryScoreItem>>((acc, item) => {
    if (!acc[item.mapFeatureName]) {
      acc[item.mapFeatureName] = item
      acc[item.mapFeatureName.toLowerCase()] = item
    }
    return acc
  }, {})
})

const activeTooltipCountry = computed(() => {
  return topCountryById.value[tooltip.value.countryId]
})

const mapSeriesData = computed(() =>
  topCountries.value
    .filter((item) => item.mapFeatureName)
    .reduce<
      Array<{
        name: string
        value: number
        itemStyle: { areaColor: string }
        emphasis: { itemStyle: { areaColor: string; borderColor: string; borderWidth: number } }
      }>
    >((items, item) => {
      if (items.some((seriesItem) => seriesItem.name === item.mapFeatureName)) {
        return items
      }
      const areaColor = getMapAreaColor(item.value)

      items.push({
        name: item.mapFeatureName,
        value: item.value,
        itemStyle: {
          areaColor,
        },
        emphasis: {
          itemStyle: {
            areaColor,
            borderColor: '#168cff',
            borderWidth: 2,
          },
        },
      })

      return items
    }, []),
)

let mapChart: any = null
const mapCountryCenterByName = new Map<string, [number, number]>()

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max)
}

const showTooltip = (item: CountryScoreItem, x: number) => {
  const mapBox = mapBoxRef.value

  if (!mapBox) return

  tooltip.value = {
    show: true,
    x: clamp(x, MAP_TOOLTIP_HALF_WIDTH + 12, mapBox.clientWidth - MAP_TOOLTIP_HALF_WIDTH - 12),
    // tips card bottom is anchored at the vertical center of the map box (card translates up 100%)
    y: mapBox.clientHeight / 2,
    countryId: item.id,
  }
}

const showTooltipAtCountry = (item: CountryScoreItem) => {
  const mapBox = mapBoxRef.value
  const position = getMapCountryTooltipPosition(item)

  if (!mapBox || !position) {
    return
  }

  showTooltip(item, position.x)
}

/** 地图 hover 国家时，把右侧列表中对应国家定位到可视区域中间。 */
const scrollCountryScoreRowIntoView = (countryId: string) => {
  const listEl = countryScoreListRef.value

  if (!listEl || lastScrolledCountryId.value === countryId) return

  const rowEl = Array.from(listEl.querySelectorAll<HTMLElement>('[data-country-id]')).find(
    (el) => el.dataset.countryId === countryId,
  )

  if (!rowEl) return

  const nextScrollTop = rowEl.offsetTop - (listEl.clientHeight - rowEl.offsetHeight) / 2
  listEl.scrollTo({
    top: clamp(nextScrollTop, 0, listEl.scrollHeight - listEl.clientHeight),
    behavior: 'auto',
  })
  lastScrolledCountryId.value = countryId
  syncCountryScoreScrollMetrics()
}

const handleMapHover = (params: EchartsMouseParams) => {
  const mapName = normalizeText(params.name)
  const matched = topCountryByMapName.value[mapName] || topCountryByMapName.value[mapName.toLowerCase()]
  const mapBox = mapBoxRef.value

  if (matched && mapBox) {
    hoveredCountryId.value = matched.id
    scrollCountryScoreRowIntoView(matched.id)
    showTooltip(
      matched,
      params.event?.offsetX ?? mapBox.clientWidth / 2,
    )
  } else {
    tooltip.value.show = false
  }
}

const handleMapLeave = () => {
  hoveredCountryId.value = ''
  lastScrolledCountryId.value = ''
  tooltip.value.show = false
}

const handleRowHover = (item: CountryScoreItem) => {
  hoveredCountryId.value = item.id.toLowerCase()
  showTooltipAtCountry(item)
  highlightMapCountry(item)
}

const handleRowLeave = () => {
  hoveredCountryId.value = ''
  tooltip.value.show = false
  clearMapHighlight()
}

const legendItems = [
  { value: '0', color: '#e6e7eb' },
  { value: '2', color: '#e8313233' },
  { value: '4', color: '#e8313266' },
  { value: '6', color: '#e8313299' },
  { value: '8', color: '#e83132cc' },
  { value: '10', color: '#e83132' },
]

const COUNTRY_SCORE_SCROLLBAR_TRACK_HEIGHT = 378
const COUNTRY_SCORE_SCROLLBAR_THUMB_HEIGHT = 167

const syncCountryScoreScrollMetrics = () => {
  const listEl = countryScoreListRef.value

  if (!listEl) {
    countryScoreScrollMetrics.value = {
      scrollTop: 0,
      scrollHeight: 0,
      clientHeight: 0,
    }
    return
  }

  countryScoreScrollMetrics.value = {
    scrollTop: listEl.scrollTop,
    scrollHeight: listEl.scrollHeight,
    clientHeight: listEl.clientHeight,
  }
}

const collectCoordinatePairs = (coordinates: unknown, pairs: Array<[number, number]>) => {
  if (!Array.isArray(coordinates)) return

  const [lng, lat] = coordinates
  if (typeof lng === 'number' && typeof lat === 'number') {
    pairs.push([lng, lat])
    return
  }

  coordinates.forEach((item) => collectCoordinatePairs(item, pairs))
}

const getFeatureCenter = (feature: GeoJsonFeature) => {
  const pairs: Array<[number, number]> = []
  collectCoordinatePairs(feature.geometry?.coordinates, pairs)

  if (pairs.length === 0) return null

  const bounds = pairs.reduce(
    (acc, [lng, lat]) => ({
      minLng: Math.min(acc.minLng, lng),
      maxLng: Math.max(acc.maxLng, lng),
      minLat: Math.min(acc.minLat, lat),
      maxLat: Math.max(acc.maxLat, lat),
    }),
    {
      minLng: Number.POSITIVE_INFINITY,
      maxLng: Number.NEGATIVE_INFINITY,
      minLat: Number.POSITIVE_INFINITY,
      maxLat: Number.NEGATIVE_INFINITY,
    },
  )

  return [(bounds.minLng + bounds.maxLng) / 2, (bounds.minLat + bounds.maxLat) / 2] as [number, number]
}

const syncMapCountryCenters = (registeredMap: RegisteredWorldMap | undefined) => {
  mapCountryCenterByName.clear()
  const features = registeredMap?.geoJSON?.features || registeredMap?.geoJson?.features || []

  features.forEach((feature) => {
    const name = normalizeText(feature.properties?.name)
    const center = getFeatureCenter(feature)

    if (name && center) {
      mapCountryCenterByName.set(name, center)
    }
  })
}

const getMapCountryTooltipPosition = (item: CountryScoreItem) => {
  if (!mapChart || !mapChartRef.value) return null

  const center =
    item.tooltipCenter ||
    mapCountryCenterByName.get(item.mapFeatureName) ||
    mapCountryCenterFallbacks[item.mapFeatureName]
  if (!center) return null

  const point = mapChart.convertToPixel({ seriesIndex: 0 }, center)
  if (!Array.isArray(point) || typeof point[0] !== 'number' || typeof point[1] !== 'number') return null

  const chartOffsetX = item.tooltipCenter ? mapChartRef.value.offsetLeft : 0
  const chartOffsetY = item.tooltipCenter ? mapChartRef.value.offsetTop : 0
  const independentRegionGap = item.independentHighlight ? INDEPENDENT_REGION_TOOLTIP_GAP : 0

  return {
    x: point[0] + chartOffsetX,
    y: point[1] + chartOffsetY - independentRegionGap,
  }
}

const getMapOption = (): EChartsOption => ({
  tooltip: {
    show: false,
    trigger: 'item',
  },
  visualMap: {
    show: false,
    type: 'piecewise',
    pieces: [
      { gt: 8, color: '#e83132' },
      { gt: 6, lte: 8, color: '#e83132cc' },
      { gt: 4, lte: 6, color: '#e8313299' },
      { gt: 2, lte: 4, color: '#e8313266' },
      { gt: 0, lte: 2, color: '#e8313233' },
      { lte: 0, color: '#e6e7eb' },
    ],
  },
  series: [
    {
      name: 'World',
      type: 'map',
      map: 'world',
      selectedMode: false,
      zoom: mapZoom.value,
      roam: false,
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      label: {
        show: false,
      },
      itemStyle: {
        areaColor: '#e6e7eb',
        borderColor: '#ffffff',
        borderWidth: 0.8,
      },
      emphasis: {
        disabled: false,
        label: {
          show: false,
        },
        itemStyle: {
          areaColor: '#e6e7eb',
          borderColor: '#168cff',
          borderWidth: 2,
        },
      },
      select: {
        disabled: true,
        label: {
          show: false,
        },
      },
      data: mapSeriesData.value,
    },
  ],
})

const loadWorldMapScript = () => {
  if (!import.meta.client) {
    return Promise.reject(new Error('World map can only be loaded on the client'))
  }

  if (worldMapLoadPromise) {
    return worldMapLoadPromise
  }

  worldMapLoadPromise = new Promise((resolve, reject) => {
    const existedScript = document.querySelector<HTMLScriptElement>(`script[src="${WORLD_MAP_CDN_URL}"]`)

    if (existedScript?.dataset.loaded === 'true') {
      resolve()
      return
    }

    const script = existedScript || document.createElement('script')
    script.src = WORLD_MAP_CDN_URL
    script.async = true
    script.dataset.worldMap = 'true'
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true'
        resolve()
      },
      { once: true },
    )
    script.addEventListener(
      'error',
      () => {
        worldMapLoadPromise = null
        reject(new Error('Failed to load world map script'))
      },
      { once: true },
    )

    if (!existedScript) {
      document.head.appendChild(script)
    }
  })

  return worldMapLoadPromise
}

const ensureMapChart = async () => {
  if (!import.meta.client || !mapChartRef.value) return null

  const { init, load } = useEcharts()
  const echarts = await load()
  ;(window as any).echarts = echarts

  if (!echarts.getMap('world')) {
    await loadWorldMapScript()
  }

  if (!echarts.getMap('world')) {
    return null
  }

  syncMapCountryCenters(echarts.getMap('world') as RegisteredWorldMap)
  const chartProxy = init(mapChartRef.value, { lazy: true, renderer: 'canvas' })
  if (!chartProxy) return null
  mapChart = chartProxy

  void (async () => {
    const chart = await chartProxy.ready()
    if (!chart) return
    chart.off('mouseover')
    chart.off('mouseout')
    chart.on('mouseover', handleMapHover)
    chart.on('mouseout', handleMapLeave)
    mapChartReady.value = true
  })().catch(() => undefined)

  return chartProxy
}

const renderMapChart = async () => {
  const chart = mapChart || (await ensureMapChart())

  if (!chart) return

  chart.setOption(getMapOption(), true)
  chart.resize()
  mapChartLoadFailed.value = false
}

const safeRenderMapChart = () => {
  void renderMapChart().catch(() => {
    mapChartLoadFailed.value = true
  })
}

const resizeMapChart = () => {
  mapChart?.resize()
}

const clearMapHighlight = () => {
  if (!mapChart) return

  mapChart.dispatchAction({
    type: 'downplay',
    seriesIndex: 0,
  })
  mapChart.setOption({
    series: [
      {
        markPoint: {
          data: [],
        },
      },
    ],
  })
}

const highlightMapCountry = (item: CountryScoreItem) => {
  if (!mapChart || !mapChartReady.value) return

  clearMapHighlight()

  if (item.independentHighlight && item.tooltipCenter) {
    mapChart.setOption({
      series: [
        {
          markPoint: {
            silent: true,
            symbol: 'circle',
            symbolSize: item.mapFeatureName === 'Taiwan' ? [10, 18] : [12, 12],
            symbolRotate: item.mapFeatureName === 'Taiwan' ? -18 : 0,
            label: {
              show: false,
            },
            itemStyle: {
              color: '#168cff',
              borderColor: '#ffffff',
              borderWidth: 1,
            },
            data: [{ coord: item.tooltipCenter }],
          },
        },
      ],
    })
    return
  }

  mapChart.dispatchAction({
    type: 'highlight',
    seriesIndex: 0,
    name: item.mapFeatureName,
  })
}

const disposeMapChart = () => {
  mapChart?.dispose()
  mapChart = null
  mapChartReady.value = false
}

const showCountryScoreScrollbar = computed(() => {
  const { scrollHeight, clientHeight } = countryScoreScrollMetrics.value

  return scrollHeight > clientHeight + 1
})

const countryScoreThumbStyle = computed(() => {
  const { scrollTop, scrollHeight, clientHeight } = countryScoreScrollMetrics.value

  if (scrollHeight <= clientHeight || clientHeight <= 0) {
    return {
      height: '0px',
      transform: 'translateY(0px)',
    }
  }

  const maxScrollTop = scrollHeight - clientHeight
  const maxThumbOffset = COUNTRY_SCORE_SCROLLBAR_TRACK_HEIGHT - COUNTRY_SCORE_SCROLLBAR_THUMB_HEIGHT
  const thumbOffset = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * maxThumbOffset : 0

  return {
    height: `${COUNTRY_SCORE_SCROLLBAR_THUMB_HEIGHT}px`,
    transform: `translateY(${thumbOffset}px)`,
  }
})

onMounted(() => {
  nextTick(() => {
    syncCountryScoreScrollMetrics()
    safeRenderMapChart()
  })

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', syncCountryScoreScrollMetrics)
    window.addEventListener('resize', resizeMapChart)
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', syncCountryScoreScrollMetrics)
    window.removeEventListener('resize', resizeMapChart)
  }
  disposeMapChart()
})

watch(
  topCountries,
  () => {
    nextTick(() => {
      syncCountryScoreScrollMetrics()
      safeRenderMapChart()
    })
  },
  { immediate: true },
)
</script>

<template>
  <section class="exchange-business-card" aria-labelledby="business-area-title">
    <header class="business-card-header">
      <div class="business-card-title-group">
        <h2 id="business-area-title" class="business-title">{{ t('82587') }}</h2>
        <span v-if="showInfluenceRating" class="influence-badge">
          <span>{{ t('83880') }}</span>
          <strong class="badge-bold">{{ props.influenceRating }}</strong>
        </span>
      </div>
      <span class="business-header-icon" aria-hidden="true"></span>
    </header>

    <div class="business-card-body">
      <div ref="mapBoxRef" class="map-interactive-box">
        <div id="appRaisal" ref="mapChartRef" class="business-map" :style="mapStyle" aria-label="World map"></div>

        <div class="map-legend">
          <div v-for="item in legendItems" :key="item.value" class="legend-item">
            <span class="legend-color-block" :style="{ backgroundColor: item.color }"></span>
            <span class="legend-text">{{ item.value }}</span>
          </div>
        </div>

        <div
          v-if="tooltip.show && activeTooltipCountry"
          class="map-tooltip"
          :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
        >
          <span class="tooltip-flag" :style="{ backgroundColor: activeTooltipCountry.flagBg || 'transparent' }">
            <CommonImage
              v-if="activeTooltipCountry.flag"
              :src="activeTooltipCountry.flag"
              class="tooltip-flag-img"
              :alt="activeTooltipCountry.name"
              width="32"
              height="32"
              loading="lazy"
            />
          </span>
          <span class="tooltip-copy">
            <span class="tooltip-name">{{ activeTooltipCountry.tooltipName || activeTooltipCountry.name }}</span>
            <span class="tooltip-val">{{ activeTooltipCountry.tooltipValue || activeTooltipCountry.score }}</span>
          </span>
        </div>
      </div>

      <aside class="country-score-panel" :class="{ 'country-score-panel-rtl': isRtl }">
        <div class="country-score-header">
          <span>{{ t('82589') }}</span>
          <span>{{ t('82590') }}</span>
        </div>

        <div ref="countryScoreListRef" class="country-score-list" @scroll="syncCountryScoreScrollMetrics">
          <div
            v-for="item in topCountries"
            :key="item.id"
            class="country-score-row"
            :class="{ 'row-highlight': hoveredCountryId === item.id }"
            :data-country-id="item.id"
            tabindex="0"
            @focus="handleRowHover(item)"
            @blur="handleRowLeave"
            @mouseenter="handleRowHover(item)"
            @mouseover="handleRowHover(item)"
            @mouseleave="handleRowLeave"
            @pointerenter="handleRowHover(item)"
            @pointerover="handleRowHover(item)"
            @pointerleave="handleRowLeave"
          >
            <span class="country-flag" :style="{ backgroundColor: item.flagBg || 'transparent' }">
              <CommonImage
                v-if="item.flag"
                :src="item.flag"
                class="country-flag-img"
                :alt="item.name"
                width="28"
                height="28"
                loading="lazy"
              />
            </span>

            <span class="country-name">{{ item.name }}</span>
            <span class="score-text">{{ item.score }}</span>
          </div>
        </div>
        <div v-if="showCountryScoreScrollbar" class="country-score-scrollbar" aria-hidden="true">
          <div class="country-score-scrollbar-thumb" :style="countryScoreThumbStyle"></div>
        </div>
      </aside>
    </div>
  </section>
</template>

<style scoped lang="less">
.exchange-business-card {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  border-radius: 12px;
  background: #fff;
  box-shadow: inset 0 0 0 1px var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  color: #292a2e;
}

.business-card-header {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 0.5px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
}

.business-card-title-group {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 16px;
  white-space: nowrap;
}

.business-title {
  margin: 0;
  color: #292a2e;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.influence-badge {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 1000px;
  background: #f8f8f8;
  color: #505258;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.badge-bold {
  color: #292a2e;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
}

.business-header-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.business-card-body {
  display: flex;
  width: 100%;
  height: 426px;
  align-items: center;
  gap: 24px;
}

.map-interactive-box {
  position: relative;
  min-width: 0;
  height: 100%;
  flex: 1;
  overflow: hidden;
  border-radius: 8px;
  background: #fff;
}

.business-map {
  position: absolute;
  top: 20px;
  left: 20px;
  width: calc(100% - 40px);
  height: 368px;
  transform-origin: center center;
  transition: transform 0.24s ease;
}

.map-load-empty {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  color: #7d818a;
  font-size: 14px;
  line-height: 22px;
  transform: translate(-50%, -50%);
}

.map-tooltip {
  position: absolute;
  z-index: 4;
  display: flex;
  pointer-events: none;
  width: 150px;
  height: 68px;
  box-sizing: border-box;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(11, 18, 40, 0.12);
  transform: translate(-50%, -100%);

  &::after {
    position: absolute;
    bottom: -6px;
    left: 50%;
    width: 12px;
    height: 12px;
    background: #fff;
    box-shadow: 4px 4px 8px rgba(11, 18, 40, 0.06);
    content: '';
    transform: translateX(-50%) rotate(45deg);
  }

  .tooltip-flag {
    position: relative;
    z-index: 1;
    display: inline-flex;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    border-radius: 100px;
  }

  .tooltip-flag-img {
    width: 100%;
    height: 100%;
    border-radius: 100px;
    object-fit: cover;
  }

  .tooltip-copy {
    position: relative;
    z-index: 1;
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    justify-content: center;
    white-space: nowrap;
  }

  .tooltip-name {
    max-width: 78px;
    overflow: hidden;
    color: #7d818a;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    text-overflow: ellipsis;
  }

  .tooltip-val {
    color: #292a2e;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
  }
}

.map-legend {
  position: absolute;
  bottom: 6px;
  left: 50%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  transform: translateX(-50%);
}

.legend-item {
  display: flex;
  width: 76px;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.legend-color-block {
  width: 100%;
  height: 4px;
  border-radius: 1px;
}

.legend-text {
  width: 100%;
  color: #7d818a;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
}

.map-zoom-control {
  position: absolute;
  top: 50%;
  right: 8px;
  display: flex;
  width: 28px;
  flex-direction: column;
  gap: 8px;
  padding: 4px;
  border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  border-radius: 4px;
  background: #fff;
  transform: translateY(-50%);
}

.zoom-button {
  display: flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  appearance: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:disabled {
    cursor: default;
    opacity: 1;
  }
}

.zoom-symbol {
  position: relative;
  display: block;
  flex-shrink: 0;
  color: #292a2e;
}

.zoom-symbol::before,
.zoom-symbol::after {
  position: absolute;
  top: 50%;
  left: 50%;
  content: '';
  background: currentColor;
  border-radius: 999px;
  transform: translate(-50%, -50%);
}

.zoom-symbol-plus {
  width: 11.67px;
  height: 11.67px;
}

.zoom-symbol-plus::before {
  width: 11.67px;
  height: 1.67px;
}

.zoom-symbol-plus::after {
  width: 1.67px;
  height: 11.67px;
}

.zoom-symbol-minus {
  width: 11.67px;
  height: 1.67px;
}

.zoom-symbol-minus::before {
  width: 11.67px;
  height: 1.67px;
}

.zoom-symbol-minus::after {
  display: none;
}

.country-score-panel {
  position: relative;
  display: flex;
  width: 320px;
  height: 426px;
  flex-shrink: 0;
  flex-direction: column;
  gap: 0;
  overflow: hidden;
  padding: 20px 20px 0;
  border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  border-radius: 8px;
  background: #fff;
}

.country-score-header {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 0.5px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  color: #7d818a;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.country-score-list {
  display: flex;
  width: 100%;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.country-score-scrollbar {
  position: absolute;
  top: 49.5px;
  right: -0.5px;
  display: flex;
  width: 4px;
  height: 378px;
  align-items: flex-start;
  justify-content: center;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.05);
  pointer-events: none;
}

.country-score-panel-rtl .country-score-scrollbar,
:global(html[dir='rtl']) .country-score-scrollbar {
  right: auto;
  left: -0.5px;
}

.country-score-scrollbar-thumb {
  width: 4px;
  flex: 1 0 0;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.country-score-row {
  position: relative;
  display: flex;
  width: 100%;
  height: 52.5px;
  box-sizing: border-box;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-bottom: 0.5px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease;

  &:hover,
  &:focus-visible,
  &.row-highlight {
    border-bottom-color: transparent;
    border-radius: 8px;
    background: #f8f8f8;
  }

  &:focus-visible {
    outline: 2px solid rgba(36, 104, 222, 0.28);
    outline-offset: 2px;
  }
}

.country-flag {
  position: relative;
  display: inline-flex;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  border: 0.5px solid rgba(5, 21, 36, 0.12);
}

.country-flag-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 100px;
}

.country-name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.score-text {
  width: 32px;
  flex-shrink: 0;
  color: #292a2e;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  text-align: right;
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .business-card-body {
    height: 426px;
    flex-direction: row;
  }

  .country-score-panel {
    width: 320px;
    height: 426px;
  }
}

@media screen and (max-width: 799px) {
  .exchange-business-card {
    gap: 16px;
    padding: 20px;
  }

  .business-card-title-group {
    gap: 12px;
  }

  .business-card-body {
    height: auto;
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }

  .map-interactive-box {
    height: 360px;
  }

  .business-map {
    top: 20px;
    left: 20px;
    width: calc(100% - 40px);
    height: 280px;
  }

  .map-legend {
    width: 100%;
    padding: 0 8px;
  }

  .legend-item {
    width: calc((100% - 20px) / 6);
  }

  .country-score-panel {
    width: 100%;
    height: auto;
  }
}
</style>
